<template>
  <b-card
    class="card-table table-link"
    :data-title="table.label"
    no-body
    @click="onCardClick"
  >
    <b-card-body class="pb-0">
      <b-card-title title-tag="h5">
        <font-awesome-icon :icon="['fas', table.icon || 'table']" />
        {{ table.label }}
      </b-card-title>
      <b-tooltip
        v-if="isTooltipVisible"
        :target="`${table.label}--description`"
        :title="table.description"
      />
      <b-card-text
        v-if="table.description"
        :id="`${table.label}--description`"
        ref="description"
        class="description"
      >
        {{ table.description }}
      </b-card-text>
    </b-card-body>
    <b-card-body
      v-if="canChangePermission || canEdit || canDelete"
      class="action-wrapper"
    />
    <b-card-body class="d-flex justify-content-between align-items-center">
      <div class="tags-container d-flex flex-wrap">
        <template v-for="tag in allTags">
          <b-tooltip
            v-if="isTagTooltipVisible(tag.tagGuid)"
            :target="tag.tagGuid"
            triggers="hover"
          >
            {{ tag.tagName }}
          </b-tooltip>
          <span
            :id="tag.tagGuid"
            :key="tag.tagGuid"
            :ref="`tooltip-${tag.tagGuid}`"
            class="bolt-pill"
            :style="{
              color: tag.textColor,
              borderColor: tag.textColor,
              backgroundColor: tag.backgroundColor
            }"
          >
            {{ tag.tagName }}
          </span>
        </template>
      </div>
      <b-dropdown
        v-if="canChangePermission || canEdit || canDelete"
        :id="`${idTemplate}-actions`"
        no-caret
        variant="outline-primary"
        size="sm"
      >
        <template #button-content>
          <font-awesome-icon :icon="['fas', 'cog']" />
        </template>
        <b-dropdown-item
          v-if="canChangePermission"
          :id="`${idTemplate}-permissions`"
          size="sm"
          variant="outline-secondary"
          :to="{ name: 'dataManagementPermissions', params: { itemId: table.entityGuid, type: permissionItemTypes.table } }"
        >
          Permissions
          <font-awesome-icon :icon="['fas', 'key']" />
        </b-dropdown-item>
        <b-dropdown-item
          v-if="canEdit && !isLoanCalculatorModule"
          :id="`${idTemplate}-edit`"
          size="sm"
          variant="outline-primary"
          :to="getTableUpdateViewRoute"
        >
          Edit
          <font-awesome-icon :icon="['fas', 'pencil-alt']" />
        </b-dropdown-item>
        <b-dropdown-item
          v-if="canDelete && !isLoanCalculatorModule"
          :id="`${idTemplate}-delete`"
          size="sm"
          variant="outline-danger"
          @click.stop="deleteTableCard"
        >
          Delete
          <font-awesome-icon :icon="['fas', 'trash']" />
        </b-dropdown-item>
      </b-dropdown>
    </b-card-body>
  </b-card>
</template>

<script>

import {createNamespacedHelpers} from 'vuex';

const {
  mapState: dataManagementMapState,
  mapActions: dataManagementMapActions,
} = createNamespacedHelpers('dataManagement');
const {
  mapState: sidebarMapState,
  mapActions: sidebarMapActions,
} = createNamespacedHelpers('sidebar');

export default {
  name: 'NewTableItem',
  props: {
    table: {
      type: Object,
      required: true,
    },
    idx: Number,
  },
  data() {
    return {
      idTemplate: null,
      isTooltipVisible: false,
      allTags: this.table.tags,
    };
  },
  watch: {
    selectedFolder: {
      handler(val) {
        const currentFolderId = val || localStorage.getItem('folder');
        this.$nextTick(()=>{
          const $folderElement = document.getElementById(`${currentFolderId}_folder-header`);          
          if ($folderElement) {   
            $folderElement.scrollIntoView({
              block: 'start',
              behavior: 'smooth',
              alignToTop: true,
            });
          }
        });
      },
      immediate: true,
    },
  },
  computed: {
    ...dataManagementMapState({
      permissionTypes: state => state.permissionTypes,
      permissionItemTypes: state => state.permissionItemTypes,
    }),
    ...sidebarMapState({
      selectedFolder: state => state.selectedFolder,
    }),
    isPowerAppsEntity() {
      const entityConfigJSON = JSON.parse(this.table.entityConfig);
      return entityConfigJSON.EntityType === 'powerApp';
    },
    isPowerBiReportEntity() {
      const entityConfigJSON = JSON.parse(this.table.entityConfig);
      return entityConfigJSON.EntityType === 'powerBiReport';
    },
    getTableUpdateViewRoute() {     
      // localStorage.setItem('folder', this.table.folderGuid);
      localStorage.setItem('table_rights', this.table.currentUserPermissionLevel);
      const name = this.isPowerAppsEntity
        ? 'dataManagementBuilderPowerApps.update'
        : this.isPowerBiReportEntity
          ? 'dataManagementBuilderPowerBiReport.update'
          : 'dataManagementBuilder.update';
      return {
        name,
        params: {tableUuid: this.table.entityGuid},
        query: {moduleUuid: this.table.folderGuid},
      };
    },
    canChangePermission() {
      return this.table.currentUserPermissionLevel === this.permissionTypes.admin;
    },
    canEdit() {
      return [
        this.permissionTypes.admin,
        this.permissionTypes.edit,
      ].includes(this.table.currentUserPermissionLevel);
    },
    canDelete() {
      return this.table.currentUserPermissionLevel === this.permissionTypes.admin;
    },
    getTableViewRoute() {
      const name = this.isPowerAppsEntity
        ? 'dataManagementPowerAppsTableView'
        : this.isPowerBiReportEntity
          ? 'dataManagementPowerBiReportView'
          : 'dataManagementTableView';

      const pathConfig = {
        name,
        params: {
          tableId: this.table.entityGuid,
          moduleUuid: this.table.moduleGuid,
        },
        query: {
          moduleUuid: this.table.moduleGuid,
        },
      };
      const defaultView = this.table?.views?.find(el => el.isDefault);
      if (defaultView) {
        pathConfig.query.view = defaultView.viewGuid;
      }
      return pathConfig;
    },
    isLoanCalculatorModule() {
      return this.$route.path.includes('loan-calculator');
    },
  },
  methods: {
    ...sidebarMapActions([
      'setSelectedTableAction',
      'getModuleDetails',
      'getSidebarStructure',
    ]),
    ...dataManagementMapActions(['deleteTable']),
    onCardClick() {
      this.setSelectedTableAction(this.table);
      localStorage.setItem('folder', this.table.folderGuid);
      this.$router.push(this.getTableViewRoute);
    },
    isTagTooltipVisible(uuid) {
      const currentTooltip = this.$refs[`tooltip-${uuid}`];
      if (!currentTooltip) {
        return false;
      }
      return currentTooltip[0].scrollWidth > currentTooltip[0].offsetWidth;
    },
    async updateData() {
      await this.getModuleDetails(this.table.moduleGuid);
      await this.getSidebarStructure();
    },
    async deleteTableCard() {
      const success = await this.$confirm(
        'Deleting Table...',
        `You are deleting Table ${this.table.label}. Are you sure you want to continue?`,
      );
      if (success) {
        const {success} = await this.deleteTable(this.table);
        if (success) {
          this.$toasted.success(`Table "${this.table.label}" was deleted`, {
            duration: TOASTER_DURATION,
          });
          await this.updateData();
        } else {
          this.$toasted.success('Table can not be deleted', {
            duration: TOASTER_DURATION,
          });
        }
      }
    },
  },
  mounted() {
    const $item = this.$refs.description;
    if (!$item) {
      this.isTooltipVisible = false;
    } else {
      this.isTooltipVisible = $item.scrollHeight > $item.offsetHeight;
    }

    const folderName = this.$options.filters.displayName(this.table.folderName);
    const tableName = this.$options.filters.displayName(this.table.label);
    this.idTemplate = `${folderName}-${tableName}`;

    const currentFolderId = localStorage.getItem('folder');
    this.$nextTick(()=>{
      const $folderElement = document.getElementById(`${currentFolderId}_folder-header`);          
      if ($folderElement) {   
        $folderElement.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
          alignToTop: true,
        });
      }
    });
  },
};
</script>

<style scoped lang="scss">
@import "../../../../../core/scss/variables";

.card-table {
  cursor: pointer;
  transition: 0.3s;
  border-radius: 0;
  min-height: 10rem;

  .description {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &:hover {
    box-shadow: 0 0 0 2px $primary;
  }
}

.action-wrapper {
  height: fit-content;
  padding-top: 0;
  display: flex;
  align-items: flex-end;
  gap: 5px;
  justify-content: flex-end;
}

.table-link {
  text-decoration: none;
  color: unset;
}

.bolt-pill {
  display: inline-block;
  align-items: center;
  padding: 0.25em 0.75em;
  max-width: 100px;
  font-size: 0.675rem;
  color: #333;
  background-color: #f3f2f1;
  border: 1px solid #d1d1d1;
  border-radius: 12px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.bolt-pill:hover {
  background-color: #e1dfdd;
}

.tags-container {
  gap: 5px;
}
</style>